import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Plus, Send, CheckCircle, XCircle, RotateCcw, FileText, DollarSign,
  AlertTriangle, Lock, Eye, ClipboardList, ShieldAlert, FileWarning, ShieldCheck,
  PackageCheck, FileDown, TrendingUp, Ban, Zap, Mail,
} from 'lucide-react';
import { ProximoBoletimPanel } from './ProximoBoletimPanel';
import { SaldosContratoPanel } from './SaldosContratoPanel';
import { QuickCaptureModal } from './QuickCaptureModal';
import { useProximoBoletim } from '@/hooks/useProximoBoletim';
import { GlosaDialog } from './GlosaDialog';
import { RegistryStateChip } from '@/components/system/RegistryStateChip';
import { ITEM_STATUS_LABELS, ITEM_STATUS_COLORS } from '@/hooks/useEmpGlosarItem';
import { WorkflowTimeline } from './EmpWorkflowView';
import MetricTooltip, { METRIC_EXPLANATIONS } from '@/components/system/MetricTooltip';
import { EmpExportButton } from './EmpExportButton';
import { useEvmContrato } from '@/hooks/useEvmContrato';
import { useEmpBoletins, useEmpMedicaoItens, useEmpGlosas, useEmpMedicoesMutations,
  EmpBoletimMedicao, EmpMedicaoItem, EmpGlosa,
  BOLETIM_STATUS_LABELS, BOLETIM_STATUS_COLORS, BOLETIM_STATUS_DB
} from '@/hooks/useEmpMedicoes';
import { useGerarRelatorioPdf } from '@/hooks/useGerarRelatorioPdf';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useEmpGlosasResumo } from '@/hooks/useEmpGlosasContrato';
import { ApprovalEmailDialog } from '@/modules/financiamento/components/ApprovalEmailDialog';
import { ComprovacaoPagamentoTab } from '@/modules/financiamento/components/ComprovacaoPagamentoTab';
import { useEmpSaldoMedicao, SaldoMedicaoItem } from '@/hooks/useEmpSaldoMedicao';
import { useEmpPlanilhaVersoes, useEmpPlanilhaDetalhes, EmpPlanilhaItem, EmpPlanilhaVersao } from '@/hooks/useEmpPlanilha';
import { useConformidadeResultados, ConformidadeResultado } from '@/hooks/useConformidade';
import { useEmpPenalidadeOcorrencias } from '@/hooks/useEmpPenalidades';
import { useContractSoftWarnings } from '@/hooks/useContractSoftWarnings';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useUxAnalyticsFlag } from '@/hooks/useUxAnalyticsFlag';
import { isSliceF3AutoEnabled } from '@/lib/featureFlags';
import { CriarBoletimMinimoDialog } from './CriarBoletimMinimoDialog';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface EmpMedicoesViewProps {
  contratoId: string;
  contratoNome: string;
  canEdit: boolean;
}

const fmt = (d: string | null) => {
  if (!d) return '—';
  try { return format(new Date(d + (d.includes('T') ? '' : 'T00:00:00')), 'dd/MM/yyyy', { locale: ptBR }); }
  catch { return d; }
};
import { fmtCurrency as fmtCur } from '@/lib/formatters';
const fmtPct = (v: number | null) => `${((v ?? 0)).toFixed(2)}%`;

// ── Checklist item for the silent validation modal ──────────────────────────
interface ValidationItem {
  label: string;
  ok: boolean;
  detail?: string;
}

export const EmpMedicoesView: React.FC<EmpMedicoesViewProps> = ({ contratoId, contratoNome, canEdit }) => {
  const { toast } = useToast();
  const { data: boletins = [], isLoading } = useEmpBoletins(contratoId);
  const { data: versoes = [] } = useEmpPlanilhaVersoes(contratoId);
  const { data: conformidadeResultados = [] } = useConformidadeResultados(contratoId);
  const { data: glosasResumo } = useEmpGlosasResumo(contratoId);
  const { ocorrencias: penOcorrencias } = useEmpPenalidadeOcorrencias(contratoId);
  const mutations = useEmpMedicoesMutations(contratoId);
  // Soft warnings — used for Pilar 3 (Glosa Risk section in modal)
  const softWarnings = useContractSoftWarnings(contratoId);

  // EVM data — conditional on feature flag + contract type
  const { evmSnapshot, isEpcQtd } = useEvmContrato(contratoId);
  console.debug('[EVM:MED]', evmSnapshot ? 'EVM_SOURCE_SELECTED' : 'EVM_DATA_EMPTY');

  const [selectedBoletim, setSelectedBoletim] = useState<EmpBoletimMedicao | null>(null);

  // ── Sync selectedBoletim with fresh query data to prevent stale status ──
  useEffect(() => {
    if (selectedBoletim && boletins.length > 0) {
      const fresh = boletins.find(b => b.id === selectedBoletim.id);
      if (fresh && fresh.updated_at !== selectedBoletim.updated_at) {
        setSelectedBoletim(fresh);
      } else if (!fresh) {
        setSelectedBoletim(null);
      }
    }
  }, [boletins, selectedBoletim]);
  const [createOpen, setCreateOpen] = useState(false);
  const [createMinimoOpen, setCreateMinimoOpen] = useState(false);

  // SLICE F3 — auto-enable por ambiente OU cluster piloto.
  const { data: contratoMeta } = useQuery({
    queryKey: ['emp-contrato-cluster', contratoId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('emp_contratos')
        .select('cluster_id')
        .eq('id', contratoId)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });
  const sliceF3Enabled = isSliceF3AutoEnabled(contratoMeta?.cluster_id ?? null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false);
  const [glosaOpen, setGlosaOpen] = useState(false);
  const [warningConfirmOpen, setWarningConfirmOpen] = useState(false);
  const [quickCaptureOpen, setQuickCaptureOpen] = useState(false);
  const [approvalEmailOpen, setApprovalEmailOpen] = useState(false);
  const [approvalEmailOrigem, setApprovalEmailOrigem] = useState<'automatico' | 'manual'>('manual');
  const [motivo, setMotivo] = useState('');
  const [cockpitTab, setCockpitTab] = useState<'boletim' | 'proximo' | 'saldos'>('boletim');
  const { user } = useAuth();
  const { logUxEvent } = useUxAnalyticsFlag();

  // Próximo Boletim count for tab badge
  const { data: proximoItens = [] } = useProximoBoletim(contratoId);
  const proximoCount = proximoItens.length;

  // When selecting a boletim, switch to boletim tab
  const handleSelectBoletim = useCallback((b: EmpBoletimMedicao) => {
    setSelectedBoletim(b);
    setCockpitTab('boletim');
  }, []);

  // Silent validation modal state (B1)
  const [validationOpen, setValidationOpen] = useState(false);
  const [pendingEnviarId, setPendingEnviarId] = useState<string | null>(null);

  const versaoVigente = versoes.find(v => v.status === 'vigente');
  const planilhaData = useEmpPlanilhaDetalhes(versaoVigente?.id ?? null);
  const itens = planilhaData.itens;

  // Conformidade gate
  const conformidadeErrors = useMemo(() => conformidadeResultados.filter(r => r.severidade === 'error'), [conformidadeResultados]);
  const conformidadeWarnings = useMemo(() => conformidadeResultados.filter(r => r.severidade === 'warning'), [conformidadeResultados]);
  const hasBlockingErrors = conformidadeErrors.length > 0;
  const hasWarnings = conformidadeWarnings.length > 0;

  // Build silent validation checklist for the selected boletim (B1)
  const buildValidationChecklist = (boletim: EmpBoletimMedicao): ValidationItem[] => {
    const penAtivasCount = penOcorrencias.filter(o => !['cancelada'].includes(o.status)).length;
    const glosasAtivas = glosasResumo?.ativas ?? 0;
    const glosasContestadasCount = glosasResumo ? (glosasResumo.total - glosasResumo.ativas) : 0;

    return [
      {
        label: 'Sem erros de conformidade bloqueantes',
        ok: conformidadeErrors.length === 0,
        detail: conformidadeErrors.length > 0
          ? `${conformidadeErrors.length} erro(s) de conformidade impedem o envio`
          : undefined,
      },
      {
        label: 'Sem glosas em aberto contestando o período',
        ok: glosasAtivas === 0,
        detail: glosasAtivas > 0
          ? `${glosasAtivas} glosa(s) ativa(s) — resolva antes de enviar`
          : undefined,
      },
      {
        label: 'Sem penalidades ativas sem reconhecimento',
        ok: penAtivasCount === 0,
        detail: penAtivasCount > 0
          ? `${penAtivasCount} penalidade(s) ativa(s) — verifique a aba Penalidades`
          : undefined,
      },
      {
        label: 'Boletim possui itens de medição registrados',
        ok: true, // validated at BoletimDetail level; we trust the user filled it
        detail: undefined,
      },
    ];
  };

  // Called by BoletimDetail instead of directly firing the mutation
  const handleEnviarRequest = (boletimId: string) => {
    const boletim = boletins.find(b => b.id === boletimId) ?? selectedBoletim;
    if (!boletim) return;
    const checklist = buildValidationChecklist(boletim);
    const hasFailures = checklist.some(c => !c.ok);
    if (hasFailures) {
      setPendingEnviarId(boletimId);
      setValidationOpen(true);
    } else {
      // All good — fire silently, then auto-dispatch approval email
      mutations.enviarBoletim.mutate(boletimId, {
        onSuccess: () => {
          setApprovalEmailOrigem('automatico');
          setApprovalEmailOpen(true);
        },
      });
    }
  };

  const handleNovoBoletim = () => {
    if (hasBlockingErrors) return;
    if (hasWarnings) {
      setWarningConfirmOpen(true);
    } else if (sliceF3Enabled) {
      setCreateMinimoOpen(true);
    } else {
      setCreateOpen(true);
    }
  };

  // KPIs
  const kpis = useMemo(() => {
    const total = boletins.length;
    const aprovados = boletins.filter(b => b.status === BOLETIM_STATUS_DB.APROVADO_PARA_FATURAMENTO).length;
    const rascunhos = boletins.filter(b => b.status === BOLETIM_STATUS_DB.RASCUNHO).length;
    const valorTotal = boletins.filter(b => b.status === BOLETIM_STATUS_DB.APROVADO_PARA_FATURAMENTO).reduce((s, b) => s + (b.valor_liquido ?? 0), 0);
    return { total, aprovados, rascunhos, valorTotal };
  }, [boletins]);

  const isReadOnly = selectedBoletim?.status === BOLETIM_STATUS_DB.APROVADO_PARA_FATURAMENTO;
  const canSubmit = selectedBoletim?.status === BOLETIM_STATUS_DB.RASCUNHO;
  const canApprove = selectedBoletim?.status === BOLETIM_STATUS_DB.PENDENTE_APROVACAO_CLIENTE;

  return (
    <div className="space-y-6">
      {/* Alerta de glosas pré-medição ativas */}
      {glosasResumo && glosasResumo.temGlosaAtiva && (
        <div className="flex items-center gap-3 p-3 rounded-lg border border-destructive/40 bg-destructive/5 text-sm">
          <FileWarning className="h-4 w-4 text-destructive flex-shrink-0" />
          <div className="flex-1">
            <strong className="text-destructive">{glosasResumo.ativas} glosa{glosasResumo.ativas > 1 ? 's' : ''} ativa{glosasResumo.ativas > 1 ? 's' : ''}</strong>
            {' · '}
            <span className="text-muted-foreground">
              Esta medição possui glosas vinculadas ao Quality Gate ou registradas manualmente.
              Valor glosado: <strong>{fmtCur(glosasResumo.totalGlosado)}</strong>.
              Acesse a aba <strong>Glosas</strong> no contrato para detalhes.
            </span>
          </div>
        </div>
      )}
      {/* KPIs */}
      <div className={`grid grid-cols-2 gap-4 ${evmSnapshot ? 'md:grid-cols-3 lg:grid-cols-6' : 'md:grid-cols-4'}`}>
        <Card><CardContent className="pt-4">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Total Boletins</p>
          <p className="text-2xl font-bold text-foreground font-display">{kpis.total}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Aprovados</p>
          <p className="text-2xl font-bold text-success font-display">{kpis.aprovados}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Em Edição</p>
          <p className="text-2xl font-bold text-warning font-display">{kpis.rascunhos}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1"><DollarSign className="h-3 w-3" /> Valor Aprovado</p>
          <p className="text-xl font-bold text-foreground font-display">{fmtCur(kpis.valorTotal)}</p>
        </CardContent></Card>
        {evmSnapshot && (
          <Card><CardContent className="pt-4">
            <p className="text-sm text-muted-foreground flex items-center gap-1">
              <TrendingUp className="h-3 w-3" />
              <MetricTooltip explanation={METRIC_EXPLANATIONS.ac}>
                AC Acumulado
              </MetricTooltip>
            </p>
            <p className="text-xl font-bold">{fmtCur(evmSnapshot.ac)}</p>
          </CardContent></Card>
        )}
        {evmSnapshot && evmSnapshot.cpi != null && (
          <Card><CardContent className="pt-4">
            <p className="text-sm text-muted-foreground flex items-center gap-1">
              <DollarSign className="h-3 w-3" />
              <MetricTooltip explanation={METRIC_EXPLANATIONS.cpi}>
                CPI
              </MetricTooltip>
            </p>
            <p className={`text-2xl font-bold ${
              evmSnapshot.cpi >= 1 ? 'text-success' :
              evmSnapshot.cpi >= 0.9 ? 'text-warning' : 'text-destructive'
            }`}>
              {evmSnapshot.cpi.toFixed(2)}
            </p>
          </CardContent></Card>
        )}
      </div>


      {/* Boletins List + Detail */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left: List */}
        <Card className="lg:col-span-1">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-base">Boletins</CardTitle>
            <div className="flex gap-1">
              <EmpExportButton
                getData={() => boletins.map(b => ({
                  'Nº': b.numero_boletim,
                  'Período Início': b.periodo_inicio,
                  'Período Fim': b.periodo_fim,
                  'Status': BOLETIM_STATUS_LABELS[b.status],
                  'Valor Bruto': b.valor_bruto,
                  'Glosas': b.valor_glosas,
                  'Valor Líquido': b.valor_liquido,
                  '% Físico Período': b.percentual_fisico_periodo,
                  '% Físico Acumulado': b.percentual_fisico_acumulado,
                }))}
                filename={`boletins_medicao_${contratoNome}`}
                printTitle={`Boletins de Medição — ${contratoNome}`}
              />
              {canEdit && versaoVigente && (
                hasBlockingErrors ? (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span tabIndex={0}>
                          <Button size="sm" disabled className="pointer-events-none">
                            <ShieldAlert className="h-4 w-4 mr-1" /> Novo
                          </Button>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="max-w-xs">
                        <p className="font-semibold text-destructive mb-1">Medição bloqueada por erros de conformidade:</p>
                        <ul className="text-xs space-y-0.5">
                          {conformidadeErrors.map(e => (
                            <li key={e.id}>• [{e.regra}] {e.descricao}</li>
                          ))}
                        </ul>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : (
                  <Button size="sm" onClick={handleNovoBoletim}>
                    <Plus className="h-4 w-4 mr-1" /> Novo
                  </Button>
                )
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-2 max-h-[500px] overflow-y-auto">
            {!versaoVigente && (
              <p className="text-xs text-muted-foreground text-center py-4">
                <AlertTriangle className="h-4 w-4 mx-auto mb-1" />
                Importe e ative uma planilha antes de criar boletins.
              </p>
            )}
            {isLoading && <p className="text-sm text-muted-foreground">Carregando...</p>}
            {boletins.map(b => (
              <div
                key={b.id}
                onClick={() => handleSelectBoletim(b)}
                className={`p-3 rounded-md border cursor-pointer transition-colors hover:bg-muted/50 ${selectedBoletim?.id === b.id ? 'border-primary bg-muted/30' : ''}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-sm">Boletim #{b.numero_boletim}</span>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <Badge variant={BOLETIM_STATUS_COLORS[b.status]}>{BOLETIM_STATUS_LABELS[b.status]}</Badge>
                    <RegistryStateChip record={b} domain="boletim" />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-1">{fmt(b.periodo_inicio)} — {fmt(b.periodo_fim)}</p>
                {b.valor_liquido != null && b.valor_liquido > 0 && (
                  <p className="text-xs font-semibold text-foreground mt-1">{fmtCur(b.valor_liquido)}</p>
                )}
                {b.status === BOLETIM_STATUS_DB.APROVADO_PARA_FATURAMENTO && <Lock className="h-3 w-3 text-muted-foreground mt-1" />}
              </div>
            ))}
            {boletins.length === 0 && versaoVigente && !isLoading && (
              <p className="text-sm text-muted-foreground text-center py-4">Nenhum boletim criado.</p>
            )}
          </CardContent>
        </Card>

        {/* Right: Cockpit Panel */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader className="pb-2 space-y-2">
              <div className="flex gap-1 p-1 bg-muted rounded-lg w-fit">
                <button
                  onClick={() => setCockpitTab('boletim')}
                  className={`px-3 py-1.5 text-sm rounded-md transition-colors ${cockpitTab === 'boletim' ? 'bg-background shadow-sm font-medium' : 'text-muted-foreground hover:text-foreground'}`}
                >
                  Boletim
                </button>
                <button
                  onClick={() => {
                    setCockpitTab('proximo');
                    logUxEvent({
                      contrato_id: contratoId,
                      tipo_evento: 'cockpit_aba_proximo_acessada',
                      payload: { origem: 'cockpit_tab' },
                    });
                  }}
                  className={`px-3 py-1.5 text-sm rounded-md transition-colors flex items-center gap-1.5 ${cockpitTab === 'proximo' ? 'bg-background shadow-sm font-medium' : 'text-muted-foreground hover:text-foreground'}`}
                >
                  Próximo Boletim
                  {proximoCount > 0 && (
                    <span className="inline-flex items-center justify-center h-5 min-w-[20px] px-1 rounded-full bg-primary text-primary-foreground text-xs font-bold">
                      {proximoCount}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => {
                    setCockpitTab('saldos');
                    logUxEvent({
                      contrato_id: contratoId,
                      tipo_evento: 'cockpit_aba_saldos_acessada',
                      payload: { origem: 'cockpit_tab' },
                    });
                  }}
                  className={`px-3 py-1.5 text-sm rounded-md transition-colors ${cockpitTab === 'saldos' ? 'bg-background shadow-sm font-medium' : 'text-muted-foreground hover:text-foreground'}`}
                >
                  Saldos
                </button>
              </div>
              {/* Context indicator */}
              <p className="text-xs text-muted-foreground">
                {cockpitTab === 'boletim'
                  ? selectedBoletim
                    ? `Visualizando: Boletim BM-${String(selectedBoletim.numero_boletim).padStart(3, '0')}`
                    : 'Selecione um boletim'
                  : 'Visão consolidada do contrato'}
              </p>
            </CardHeader>
            <CardContent>
              {cockpitTab === 'boletim' && (
                <>
                  {!selectedBoletim ? (
                    <div className="flex items-center justify-center h-48 text-muted-foreground text-sm">
                      Selecione um boletim para visualizar
                    </div>
                  ) : (
                    <BoletimDetail
                      boletim={selectedBoletim}
                      contratoId={contratoId}
                      contratoNome={contratoNome}
                      canEdit={canEdit}
                      isReadOnly={isReadOnly!}
                      canSubmit={!!canSubmit}
                      canApprove={!!canApprove}
                      mutations={mutations}
                      versaoVigente={versaoVigente || null}
                      onEnviar={() => handleEnviarRequest(selectedBoletim.id)}
                      onAprovar={() => mutations.aprovarBoletim.mutate(selectedBoletim.id)}
                      onRejeitar={() => { setMotivo(''); setRejectOpen(true); }}
                      onReabrir={() => { setMotivo(''); setReopenOpen(true); }}
                      onAddGlosa={() => setGlosaOpen(true)}
                      onResendApproval={() => {
                        setApprovalEmailOrigem('manual');
                        setApprovalEmailOpen(true);
                      }}
                    />
                  )}
                </>
              )}
              {cockpitTab === 'proximo' && (
                <ProximoBoletimPanel contratoId={contratoId} />
              )}
              {cockpitTab === 'saldos' && (
                <SaldosContratoPanel contratoId={contratoId} />
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Create Boletim Dialog (legacy) — desativado quando SLICE F3 ativo */}
      <CreateBoletimDialog
        open={createOpen && !sliceF3Enabled}
        onOpenChange={setCreateOpen}
        versaoVigente={versaoVigente || null}
        nextNumber={(boletins[0]?.numero_boletim ?? 0) + 1}
        contratoId={contratoId}
        onSubmit={(data) => {
          mutations.createBoletim.mutate(data, {
            onSuccess: () => setCreateOpen(false),
          });
        }}
        isPending={mutations.createBoletim.isPending}
      />

      {/* SLICE F3 — Boletim mínimo (sentinela) */}
      {sliceF3Enabled && (
        <CriarBoletimMinimoDialog
          contratoId={contratoId}
          planilhaVersaoId={versaoVigente?.id ?? null}
          open={createMinimoOpen}
          onOpenChange={setCreateMinimoOpen}
        />
      )}
      {/* Reject Dialog */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Rejeitar Boletim</DialogTitle></DialogHeader>
          <Label>Motivo da rejeição</Label>
          <Textarea value={motivo} onChange={e => setMotivo(e.target.value)} placeholder="Descreva o motivo..." />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)}>Cancelar</Button>
            <Button variant="destructive" disabled={!motivo.trim() || mutations.rejeitarBoletim.isPending}
              onClick={() => {
                if (selectedBoletim) mutations.rejeitarBoletim.mutate({ boletimId: selectedBoletim.id, motivo }, { onSuccess: () => setRejectOpen(false) });
              }}>
              Rejeitar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reopen Dialog */}
      <Dialog open={reopenOpen} onOpenChange={setReopenOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reabrir Boletim</DialogTitle></DialogHeader>
          <Label>Motivo da reabertura</Label>
          <Textarea value={motivo} onChange={e => setMotivo(e.target.value)} placeholder="Descreva o motivo..." />
          <DialogFooter>
            <Button variant="outline" onClick={() => setReopenOpen(false)}>Cancelar</Button>
            <Button disabled={!motivo.trim() || mutations.reabrirBoletim.isPending}
              onClick={() => {
                if (selectedBoletim) mutations.reabrirBoletim.mutate({ boletimId: selectedBoletim.id, motivo }, { onSuccess: () => setReopenOpen(false) });
              }}>
              <RotateCcw className="h-4 w-4 mr-1" /> Reabrir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unified Glosa Dialog (legacy boletim-level entry point) */}
      {selectedBoletim && (
        <GlosaDialog
          open={glosaOpen}
          onOpenChange={setGlosaOpen}
          contratoId={contratoId}
          boletimId={selectedBoletim.id}
        />
      )}

      {/* Warning Confirmation Dialog */}
      <AlertDialog open={warningConfirmOpen} onOpenChange={setWarningConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-emp-warm" />
              Avisos de conformidade ativos
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                <p className="mb-2">
                  Existem {conformidadeWarnings.length} aviso(s) de conformidade. A medição pode prosseguir, mas revise os pontos abaixo:
                </p>
                <ul className="text-xs space-y-1 max-h-40 overflow-y-auto">
                  {conformidadeWarnings.map(w => (
                    <li key={w.id} className="flex items-start gap-1">
                      <span className="text-emp-warm mt-0.5">⚠</span>
                      <span><strong>[{w.regra}]</strong> {w.descricao}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => (sliceF3Enabled ? setCreateMinimoOpen(true) : setCreateOpen(true))}
            >
              Prosseguir mesmo com avisos
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Silent Validation Modal (B1) ─────────────────────────────────── */}
      <AlertDialog open={validationOpen} onOpenChange={setValidationOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-destructive" />
              Pendências impedem o envio
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 pt-1">
                <p className="text-sm text-muted-foreground">
                  Resolva as pendências abaixo antes de enviar o boletim para aprovação.
                </p>
                {pendingEnviarId && selectedBoletim && (
                  <ul className="space-y-2">
                    {buildValidationChecklist(selectedBoletim).map((item, i) => (
                      <li key={i} className="flex items-start gap-2.5">
                        {item.ok
                          ? <ShieldCheck className="h-4 w-4 text-emp-primary flex-shrink-0 mt-0.5" />
                          : <XCircle className="h-4 w-4 text-destructive flex-shrink-0 mt-0.5" />}
                        <div>
                          <p className={`text-sm font-medium ${item.ok ? 'text-foreground' : 'text-destructive'}`}>
                            {item.label}
                          </p>
                          {item.detail && (
                            <p className="text-xs text-muted-foreground">{item.detail}</p>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </AlertDialogDescription>

            {/* ── Pilar 3: Risco de Glosa Detectado (alerta amarelo não bloqueante) ── */}
            {(() => {
              const glosaRisks: string[] = [];
              if ((softWarnings.campo?.count ?? 0) > 0) {
                glosaRisks.push(`${softWarnings.campo!.count} execução(ões) sem evidência fotográfica`);
              }
              if ((softWarnings.qualidade?.count ?? 0) > 0) {
                glosaRisks.push(`${softWarnings.qualidade!.count} execução(ões) sem Quality Gate iniciado`);
              }
              const hasGlosaRisk = glosaRisks.length > 0;
              if (!hasGlosaRisk) return null;
              return (
                <div className="mt-3 rounded-lg border border-warning/40 bg-warning/10 p-3">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 text-warning flex-shrink-0 mt-0.5" />
                    <div className="space-y-1">
                      <p className="text-sm font-semibold text-warning">Risco de Glosa Detectado</p>
                      <p className="text-xs text-muted-foreground">
                        Os itens abaixo podem gerar glosas futuras. Você pode prosseguir, mas recomenda-se resolver antes da aprovação:
                      </p>
                      <ul className="text-xs text-muted-foreground list-disc list-inside space-y-0.5">
                        {glosaRisks.map((r, i) => <li key={i}>{r}</li>)}
                      </ul>
                    </div>
                  </div>
                </div>
              );
            })()}
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingEnviarId(null)}>
              Fechar e corrigir
            </AlertDialogCancel>
            {/* Allow sending if ONLY glosa risk (no blocking failures), or if user wants to proceed anyway */}
            {pendingEnviarId && selectedBoletim && (() => {
              const checklist = buildValidationChecklist(selectedBoletim);
              const hasBlockingFailures = checklist.some(c => !c.ok);
              if (hasBlockingFailures) return null; // Hard block — only close button
              // Soft warnings only — allow proceeding
              return (
                <AlertDialogAction
                  onClick={() => {
                    if (pendingEnviarId) {
                      mutations.enviarBoletim.mutate(pendingEnviarId, {
                        onSuccess: () => {
                          setApprovalEmailOrigem('automatico');
                          setApprovalEmailOpen(true);
                        },
                      });
                    }
                    setPendingEnviarId(null);
                    setValidationOpen(false);
                  }}
                >
                  Prosseguir mesmo assim
                </AlertDialogAction>
              );
            })()}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Quick Capture Modal */}
      <QuickCaptureModal
        open={quickCaptureOpen}
        onOpenChange={setQuickCaptureOpen}
        contratoId={contratoId}
      />

      {/* Approval Email Dialog */}
      {selectedBoletim && (
        <ApprovalEmailDialog
          open={approvalEmailOpen}
          onOpenChange={setApprovalEmailOpen}
          boletimId={selectedBoletim.id}
          contratoId={contratoId}
          origem={approvalEmailOrigem}
        />
      )}
    </div>
  );
};

// ============ Sub-components ============

function BoletimDetail({ boletim, contratoId, contratoNome, canEdit, isReadOnly, canSubmit, canApprove, mutations, versaoVigente, onEnviar, onAprovar, onRejeitar, onReabrir, onAddGlosa, onResendApproval }: {
  boletim: EmpBoletimMedicao;
  contratoId: string;
  contratoNome: string;
  canEdit: boolean;
  isReadOnly: boolean;
  canSubmit: boolean;
  canApprove: boolean;
  mutations: ReturnType<typeof useEmpMedicoesMutations>;
  versaoVigente: EmpPlanilhaVersao | null;
  onEnviar: () => void;
  onAprovar: () => void;
  onRejeitar: () => void;
  onReabrir: () => void;
  onAddGlosa: () => void;
  onResendApproval: () => void;
}) {
  const { gerarBoletimPdf, loading: pdfLoading } = useGerarRelatorioPdf();
  const { data: itens = [], isLoading: itensLoading } = useEmpMedicaoItens(boletim.id);
  const { data: glosas = [], isLoading: glosasLoading } = useEmpGlosas(boletim.id);
  const { itens: planilhaItens } = useEmpPlanilhaDetalhes(versaoVigente?.id || null);
  const { data: saldoItems = [] } = useEmpSaldoMedicao(versaoVigente?.id || null);

  const [tab, setTab] = useState('itens');

  const totalGlosas = glosas.reduce((s, g) => s + g.valor_glosa, 0);
  const totalBruto = itens.reduce((s, i) => s + i.valor_periodo, 0);
  const totalLiquido = totalBruto - totalGlosas;

  // Map planilha items for display
  const planilhaMap = useMemo(() => {
    const map = new Map<string, typeof planilhaItens[0]>();
    planilhaItens.forEach(pi => map.set(pi.id, pi));
    return map;
  }, [planilhaItens]);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              Boletim #{boletim.numero_boletim}
              <Badge variant={BOLETIM_STATUS_COLORS[boletim.status]}>{BOLETIM_STATUS_LABELS[boletim.status]}</Badge>
              {isReadOnly && <Lock className="h-4 w-4 text-muted-foreground" />}
            </CardTitle>
            <p className="text-sm text-muted-foreground">{fmt(boletim.periodo_inicio)} — {fmt(boletim.periodo_fim)}</p>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => gerarBoletimPdf(boletim.id, contratoId)}
              disabled={pdfLoading}
            >
              <FileDown className="h-4 w-4 mr-1" /> {pdfLoading ? 'Gerando...' : 'PDF'}
            </Button>
            {canEdit && canSubmit && (
              <Button size="sm" onClick={onEnviar} disabled={mutations.enviarBoletim.isPending}>
                <Send className="h-4 w-4 mr-1" /> Enviar
              </Button>
            )}
            {canEdit && canApprove && (
              <>
                <Button size="sm" variant="secondary" onClick={onResendApproval}>
                  <Mail className="h-4 w-4 mr-1" /> Reenviar Aprovação
                </Button>
                <Button size="sm" variant="default" onClick={onAprovar} disabled={mutations.aprovarBoletim.isPending}>
                  <CheckCircle className="h-4 w-4 mr-1" /> Aprovar
                </Button>
                <Button size="sm" variant="destructive" onClick={onRejeitar}>
                  <XCircle className="h-4 w-4 mr-1" /> Rejeitar
                </Button>
              </>
            )}
            {canEdit && boletim.status === BOLETIM_STATUS_DB.REPROVADO && (
              <Button size="sm" variant="outline" onClick={onReabrir}>
                <RotateCcw className="h-4 w-4 mr-1" /> Voltar p/ Rascunho
              </Button>
            )}
          </div>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-3 gap-3 mt-4">
          <div className="bg-muted/30 rounded-md p-3">
            <p className="text-xs text-muted-foreground">Valor Bruto</p>
            <p className="font-bold">{fmtCur(totalBruto)}</p>
          </div>
          <div className="bg-destructive/10 rounded-md p-3">
            <p className="text-xs text-muted-foreground">Glosas</p>
            <p className="font-bold text-destructive">{fmtCur(totalGlosas)}</p>
          </div>
          <div className="bg-primary/10 rounded-md p-3">
            <p className="text-xs text-muted-foreground">Valor Líquido</p>
            <p className="font-bold">{fmtCur(totalLiquido)}</p>
          </div>
        </div>

        {boletim.motivo_reabertura && (
          <div className="mt-3 bg-muted border border-border rounded-md p-3 text-sm">
            <p className="font-medium text-foreground">Motivo da reabertura:</p>
            <p className="text-muted-foreground">{boletim.motivo_reabertura}</p>
          </div>
        )}
      </CardHeader>

      <CardContent>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="resumo" className="flex items-center gap-1">
              <Eye className="h-3.5 w-3.5" /> Resumo
            </TabsTrigger>
            <TabsTrigger value="itens" className="flex items-center gap-1">
              <ClipboardList className="h-3.5 w-3.5" /> Itens ({itens.length})
            </TabsTrigger>
            {boletim.status === BOLETIM_STATUS_DB.APROVADO_PARA_FATURAMENTO && (
              <TabsTrigger value="faturamento" className="flex items-center gap-1">
                <DollarSign className="h-3.5 w-3.5" /> Comprovação
              </TabsTrigger>
            )}
            <TabsTrigger value="glosas" className="flex items-center gap-1">
              <AlertTriangle className="h-3.5 w-3.5" /> Glosas ({glosas.length})
            </TabsTrigger>
            <TabsTrigger value="execucoes" className="flex items-center gap-1">
              <PackageCheck className="h-3.5 w-3.5" /> Execuções Disponíveis
            </TabsTrigger>
          </TabsList>

          <TabsContent value="itens">
            <MedicaoItensTab
              itens={itens}
              planilhaItens={planilhaItens}
              planilhaMap={planilhaMap}
              saldoItems={saldoItems}
              boletimId={boletim.id}
              boletimStatus={boletim.status}
              isReadOnly={isReadOnly}
              canEdit={canEdit}
              mutations={mutations}
              isLoading={itensLoading}
              contratoId={contratoId}
              glosas={glosas}
            />
          </TabsContent>

          <TabsContent value="execucoes">
            <ExecucoesDisponiveisTab contratoId={contratoId} />
          </TabsContent>

          <TabsContent value="glosas">
            <GlosasTab
              glosas={glosas}
              planilhaMap={planilhaMap}
              boletimId={boletim.id}
              isReadOnly={isReadOnly}
              canEdit={canEdit}
              mutations={mutations}
              onAddGlosa={onAddGlosa}
              isLoading={glosasLoading}
            />
          </TabsContent>

          <TabsContent value="resumo">
            <ResumoTab boletim={boletim} itens={itens} glosas={glosas} planilhaMap={planilhaMap} />
          </TabsContent>

          {boletim.status === BOLETIM_STATUS_DB.APROVADO_PARA_FATURAMENTO && (
            <TabsContent value="faturamento">
              <ComprovacaoPagamentoTab
                boletimId={boletim.id}
                contratoId={contratoId}
                canUpload={canEdit}
              />
            </TabsContent>
          )}
        </Tabs>

        {/* Workflow timeline */}
        <div className="mt-4">
          <WorkflowTimeline
            entidadeTipo="boletim"
            entidadeId={boletim.id}
            contratoId={contratoId}
            canApprove={canApprove}
          />
        </div>
      </CardContent>
    </Card>
  );
}

// --- Itens Tab ---
function MedicaoItensTab({ itens, planilhaItens, planilhaMap, saldoItems, boletimId, boletimStatus, isReadOnly, canEdit, mutations, isLoading, contratoId, glosas }: {
  itens: EmpMedicaoItem[];
  planilhaItens: any[];
  planilhaMap: Map<string, any>;
  saldoItems: SaldoMedicaoItem[];
  boletimId: string;
  boletimStatus: string;
  isReadOnly: boolean;
  canEdit: boolean;
  mutations: ReturnType<typeof useEmpMedicoesMutations>;
  isLoading: boolean;
  contratoId: string;
  glosas: EmpGlosa[];
}) {
  const [editingItem, setEditingItem] = useState<string | null>(null);
  const [qty, setQty] = useState('');
  const [obs, setObs] = useState('');
  const [glosaItem, setGlosaItem] = useState<any>(null);

  // Build saldo map from VIEW
  const saldoMap = useMemo(() => {
    const map = new Map<string, SaldoMedicaoItem>();
    saldoItems.forEach(s => map.set(s.planilha_item_id, s));
    return map;
  }, [saldoItems]);

  // Build glosa map: medicao_item_id -> total glosado
  const glosaMap = useMemo(() => {
    const map = new Map<string, { total: number; count: number }>();
    glosas.forEach(g => {
      if (g.medicao_item_id) {
        const prev = map.get(g.medicao_item_id) || { total: 0, count: 0 };
        map.set(g.medicao_item_id, { total: prev.total + g.valor_glosa, count: prev.count + 1 });
      }
    });
    return map;
  }, [glosas]);

  const handleSave = (planilhaItemId: string, valorUnitario: number, acumAnterior: number) => {
    const quantidade_medida = parseFloat(qty);
    if (isNaN(quantidade_medida) || quantidade_medida < 0) return;
    mutations.upsertMedicaoItem.mutate({
      boletim_id: boletimId,
      planilha_item_id: planilhaItemId,
      quantidade_medida,
      quantidade_acumulada_anterior: acumAnterior,
      valor_unitario: valorUnitario,
      observacoes: obs || undefined,
    }, { onSuccess: () => { setEditingItem(null); setQty(''); setObs(''); } });
  };

  // Merge planilha items with measured items
  const mergedItems = useMemo(() => {
    const measured = new Map(itens.map(i => [i.planilha_item_id, i]));
    return planilhaItens.map(pi => ({
      planilhaItem: pi,
      medicao: measured.get(pi.id) || null,
      saldo: saldoMap.get(pi.id) || null,
    }));
  }, [planilhaItens, itens, saldoMap]);

  if (isLoading) return <p className="text-sm text-muted-foreground py-4">Carregando...</p>;

  if (planilhaItens.length === 0) {
    return <p className="text-sm text-muted-foreground text-center py-8">Nenhum item na planilha vigente.</p>;
  }

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Código</TableHead>
            <TableHead>Descrição</TableHead>
            <TableHead>Und</TableHead>
            <TableHead className="text-right">Contratado</TableHead>
            <TableHead className="text-right">Saldo Disponível</TableHead>
            <TableHead className="text-right">Saldo Contratual</TableHead>
            <TableHead className="text-right">Medido (período)</TableHead>
            <TableHead className="text-right">Acumulado</TableHead>
            <TableHead className="text-right">Valor</TableHead>
            <TableHead className="text-right">%</TableHead>
            <TableHead>Origem</TableHead>
            <TableHead>Status</TableHead>
            {!isReadOnly && canEdit && <TableHead></TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {mergedItems.map(({ planilhaItem: pi, medicao, saldo }) => {
            const isEditing = editingItem === pi.id;
            const acumAnterior = medicao?.quantidade_acumulada_anterior ?? 0;
            const qtyMedida = medicao?.quantidade_medida ?? 0;
            const acumAtual = medicao?.quantidade_acumulada_atual ?? acumAnterior;
            const pctExec = pi.quantidade_contratada > 0 ? (acumAtual / pi.quantidade_contratada) * 100 : 0;
            const saldoDisponivel = saldo?.saldo_disponivel ?? 0;
            const saldoContratual = saldo?.saldo_contratual ?? 0;
            const flagExcesso = saldo?.flag_execucao_maior_que_contratado ?? false;
            const saldoZero = saldoDisponivel <= 0;
            const itemGlosa = medicao ? glosaMap.get(medicao.id) : undefined;
            const hasGlosa = !!itemGlosa && itemGlosa.count > 0;

            return (
              <TableRow key={pi.id} className={`${flagExcesso ? 'bg-destructive/5' : ''} ${hasGlosa && !flagExcesso ? 'bg-warning/5' : ''}`}>
                <TableCell className="font-mono text-xs">
                  <div className="flex items-center gap-1">
                    {pi.codigo_item}
                    {flagExcesso && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <AlertTriangle className="h-3.5 w-3.5 text-destructive flex-shrink-0" />
                          </TooltipTrigger>
                          <TooltipContent side="right" className="max-w-xs">
                            <p className="text-xs font-medium text-destructive">Execução excede quantidade contratada</p>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              Executado: {saldo?.qtd_executada_acumulada?.toLocaleString('pt-BR') ?? '—'} / Contratado: {pi.quantidade_contratada.toLocaleString('pt-BR')}
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                </TableCell>
                <TableCell className="max-w-[200px] truncate text-xs">{pi.descricao}</TableCell>
                <TableCell className="text-xs">{pi.unidade}</TableCell>
                <TableCell className="text-right text-xs">{pi.quantidade_contratada.toLocaleString('pt-BR')}</TableCell>
                <TableCell className="text-right">
                  <Badge variant={saldoZero ? 'destructive' : 'outline'} className="text-xs font-mono">
                    {saldoDisponivel.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </Badge>
                </TableCell>
                <TableCell className="text-right text-xs font-mono">
                  {saldoContratual.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </TableCell>
                <TableCell className="text-right">
                  {isEditing ? (
                    <Input type="number" step="0.01" min="0" value={qty} onChange={e => setQty(e.target.value)} className="w-24 h-8 text-right" autoFocus />
                  ) : (
                    <span className="text-xs">{qtyMedida.toLocaleString('pt-BR')}</span>
                  )}
                </TableCell>
                <TableCell className="text-right text-xs">{acumAtual.toLocaleString('pt-BR')}</TableCell>
                <TableCell className="text-right text-xs">{fmtCur(medicao?.valor_periodo ?? 0)}</TableCell>
                <TableCell className="text-right">
                  <Badge variant={pctExec >= 100 ? 'success' : pctExec >= 70 ? 'success' : pctExec >= 40 ? 'progress' : pctExec > 0 ? 'warning' : 'outline'} className="text-xs">
                    {pctExec.toFixed(1)}%
                  </Badge>
                </TableCell>
                <TableCell>
                  {(() => {
                    const origem = (medicao as any)?.tipo_origem as string | undefined;
                    if (!origem) return <span className="text-[10px] text-muted-foreground">—</span>;
                    if (origem === 'ubs_ppu') {
                      return (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge variant="default" className="text-[10px] cursor-help">UBS→PPU</Badge>
                            </TooltipTrigger>
                            <TooltipContent side="top">
                              <p className="text-xs">Linha consumida via Ordem de Serviço (UBS) convertida para PPU pelo motor híbrido.</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      );
                    }
                    if (origem === 'ubs') return <Badge variant="secondary" className="text-[10px]">UBS</Badge>;
                    if (origem === 'eap') return <Badge variant="outline" className="text-[10px]">EAP</Badge>;
                    return <Badge variant="outline" className="text-[10px]">PPU</Badge>;
                  })()}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1 flex-wrap">
                    {medicao && (
                      <Badge variant={ITEM_STATUS_COLORS[(medicao as any).status_item] ?? 'outline'} className="text-xs">
                        {ITEM_STATUS_LABELS[(medicao as any).status_item] ?? (medicao as any).status_item ?? 'Em análise'}
                      </Badge>
                    )}
                    {hasGlosa && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                       <Badge variant="destructive" className="text-xs px-1.5 py-0 cursor-help">
                              <FileWarning className="h-3 w-3 mr-0.5" />
                              Glosa
                            </Badge>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="text-xs">Este item sofreu ajuste de <strong className="text-destructive">{fmtCur(-itemGlosa!.total)}</strong></p>
                            <p className="text-xs text-muted-foreground">{itemGlosa!.count} glosa{itemGlosa!.count > 1 ? 's' : ''} aplicada{itemGlosa!.count > 1 ? 's' : ''}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                </TableCell>
                {!isReadOnly && canEdit && (
                  <TableCell>
                    <div className="flex gap-1">
                    {(() => {
                      const statusItem = (medicao as any)?.status_item ?? 'em_analise_cliente';
                      const canGlosa = medicao && statusItem === 'em_analise_cliente' && boletimStatus !== 'rascunho';
                      const isAutoLocked = medicao?.origem_quantidade === 'automatico' && boletimStatus === 'rascunho';
                      if (isAutoLocked) {
                        return (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span tabIndex={0}>
                                  <Button size="sm" variant="ghost" className="h-7 px-2 pointer-events-none" disabled>
                                    <Lock className="h-3 w-3 text-muted-foreground" />
                                  </Button>
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>Quantidade autopopulada — editável apenas após retorno da fiscalização</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        );
                      }
                      if (saldoZero && !medicao) {
                        return (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span tabIndex={0}>
                                  <Button size="sm" variant="ghost" className="h-7 px-2 pointer-events-none" disabled>
                                    <Lock className="h-3 w-3" />
                                  </Button>
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>Saldo disponível esgotado</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        );
                      }
                      if (isEditing) {
                        return (
                          <>
                            <Button size="sm" variant="ghost" className="h-7 px-2"
                              onClick={() => handleSave(pi.id, pi.preco_unitario, acumAnterior)}
                              disabled={mutations.upsertMedicaoItem.isPending}>
                              <CheckCircle className="h-3 w-3" />
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setEditingItem(null)}>
                              <XCircle className="h-3 w-3" />
                            </Button>
                          </>
                        );
                      }
                      return (
                        <>
                          <Button size="sm" variant="ghost" className="h-7 px-2"
                            onClick={() => { setEditingItem(pi.id); setQty(String(qtyMedida)); setObs(medicao?.observacoes || ''); }}>
                            Medir
                          </Button>
                          {canGlosa && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button size="sm" variant="ghost" className="h-7 px-2 text-destructive"
                                    onClick={() => setGlosaItem({
                                      id: medicao!.id,
                                      planilha_item_id: pi.id,
                                      quantidade_medida: medicao!.quantidade_medida,
                                      valor_unitario: medicao!.valor_unitario,
                                      valor_periodo: medicao!.valor_periodo,
                                      item_codigo: pi.codigo_item,
                                      item_descricao: pi.descricao,
                                    })}>
                                    <Ban className="h-3 w-3" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Glosar item</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </>
                      );
                    })()}
                    </div>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <GlosaDialog
        open={!!glosaItem}
        onOpenChange={(v) => { if (!v) setGlosaItem(null); }}
        item={glosaItem}
        escopoFixo="item"
        boletimId={boletimId}
        contratoId={contratoId}
      />
    </div>
  );
}

// --- Glosas Tab ---
function GlosasTab({ glosas, planilhaMap, boletimId, isReadOnly, canEdit, mutations, onAddGlosa, isLoading }: {
  glosas: EmpGlosa[];
  planilhaMap: Map<string, any>;
  boletimId: string;
  isReadOnly: boolean;
  canEdit: boolean;
  mutations: ReturnType<typeof useEmpMedicoesMutations>;
  onAddGlosa: () => void;
  isLoading: boolean;
}) {
  const [resolveId, setResolveId] = useState<string | null>(null);
  const [resolveObs, setResolveObs] = useState('');

  const total = glosas.reduce((s, g) => s + g.valor_glosa, 0);

  if (isLoading) return <p className="text-sm text-muted-foreground py-4">Carregando...</p>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-muted-foreground">Total de glosas: <span className="font-bold text-destructive">{fmtCur(total)}</span></p>
        </div>
        {!isReadOnly && canEdit && (
          <Button size="sm" variant="outline" onClick={onAddGlosa}>
            <Plus className="h-4 w-4 mr-1" /> Nova Glosa
          </Button>
        )}
      </div>

      {glosas.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8">Nenhuma glosa registrada.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Tipo</TableHead>
              <TableHead>Descrição</TableHead>
              <TableHead className="text-right">Valor</TableHead>
              <TableHead>Status</TableHead>
              {!isReadOnly && canEdit && <TableHead></TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {glosas.map(g => (
              <TableRow key={g.id}>
                <TableCell>
                  <Badge variant="outline" className="text-xs">{g.tipo}</Badge>
                </TableCell>
                <TableCell className="max-w-[250px]">
                  <p className="text-xs">{g.descricao}</p>
                  {g.fundamentacao && <p className="text-xs text-muted-foreground mt-0.5">{g.fundamentacao}</p>}
                  {g.resolucao_observacao && <p className="text-xs text-success mt-0.5">Resolução: {g.resolucao_observacao}</p>}
                </TableCell>
                <TableCell className="text-right font-medium text-destructive">{fmtCur(g.valor_glosa)}</TableCell>
                <TableCell>
                  <Badge variant={g.status === 'resolvida' ? 'default' : g.status === 'aceita' ? 'secondary' : 'outline'}>
                    {g.status}
                  </Badge>
                </TableCell>
                {!isReadOnly && canEdit && (
                  <TableCell>
                    {g.status === 'pendente' && (
                      resolveId === g.id ? (
                        <div className="flex items-center gap-1">
                          <Input className="h-7 text-xs w-32" placeholder="Observação" value={resolveObs} onChange={e => setResolveObs(e.target.value)} />
                          <Button size="sm" variant="ghost" className="h-7 px-2"
                            onClick={() => mutations.resolverGlosa.mutate({ id: g.id, boletimId, observacao: resolveObs }, { onSuccess: () => { setResolveId(null); setResolveObs(''); } })}
                            disabled={mutations.resolverGlosa.isPending}>
                            <CheckCircle className="h-3 w-3" />
                          </Button>
                        </div>
                      ) : (
                        <Button size="sm" variant="ghost" className="h-7" onClick={() => { setResolveId(g.id); setResolveObs(''); }}>
                          Resolver
                        </Button>
                      )
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

// --- Resumo Tab ---
function ResumoTab({ boletim, itens, glosas, planilhaMap }: {
  boletim: EmpBoletimMedicao;
  itens: EmpMedicaoItem[];
  glosas: EmpGlosa[];
  planilhaMap: Map<string, any>;
}) {
  const totalBruto = itens.reduce((s, i) => s + i.valor_periodo, 0);
  const totalGlosas = glosas.reduce((s, g) => s + g.valor_glosa, 0);
  const totalLiquido = totalBruto - totalGlosas;
  const totalAcumulado = itens.reduce((s, i) => s + i.valor_acumulado, 0);

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-4 space-y-3">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-muted-foreground">Período</p>
              <p className="font-medium">{fmt(boletim.periodo_inicio)} — {fmt(boletim.periodo_fim)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Status</p>
              <Badge variant={BOLETIM_STATUS_COLORS[boletim.status]}>{BOLETIM_STATUS_LABELS[boletim.status]}</Badge>
            </div>
          </div>
          <Separator />
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="text-xs text-muted-foreground">Valor Bruto</p>
              <p className="text-lg font-bold">{fmtCur(totalBruto)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">(-) Glosas</p>
              <p className="text-lg font-bold text-destructive">{fmtCur(totalGlosas)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Valor Líquido</p>
              <p className="text-lg font-bold">{fmtCur(totalLiquido)}</p>
            </div>
          </div>
          <Separator />
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-muted-foreground">Acumulado Total</p>
              <p className="font-bold">{fmtCur(totalAcumulado)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Itens Medidos</p>
              <p className="font-bold">{itens.filter(i => i.quantidade_medida > 0).length} / {itens.length}</p>
            </div>
          </div>
          {boletim.observacoes && (
            <>
              <Separator />
              <div>
                <p className="text-xs text-muted-foreground">Observações</p>
                <p className="text-sm">{boletim.observacoes}</p>
              </div>
            </>
          )}
          {boletim.aprovado_em && (
            <>
              <Separator />
              <p className="text-xs text-muted-foreground">Aprovado em: {fmt(boletim.aprovado_em)}</p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// --- Create Boletim Dialog ---
function CreateBoletimDialog({ open, onOpenChange, versaoVigente, nextNumber, onSubmit, isPending, contratoId }: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  versaoVigente: EmpPlanilhaVersao | null;
  nextNumber: number;
  onSubmit: (data: { planilha_versao_id: string; numero_boletim: number; periodo_inicio: string; periodo_fim: string; observacoes?: string }) => void;
  isPending: boolean;
  contratoId: string;
}) {
  const [periodoInicio, setPeriodoInicio] = useState('');
  const [periodoFim, setPeriodoFim] = useState('');
  const [obs, setObs] = useState('');

  // A3+: Fetch available executions for eligibility summary
  const { data: execDisp = [] } = useQuery({
    queryKey: ['a3-eligibility', contratoId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('emp_validacoes_execucao')
        .select('id, data_execucao')
        .eq('contrato_id', contratoId)
        .eq('status', 'disponivel_para_medicao')
        .is('boletim_id', null)
        .order('data_execucao', { ascending: true });
      if (error) throw error;
      return data || [];
    },
    enabled: open && !!contratoId,
  });

  // A3+: Compute eligibility stats
  const eligibility = useMemo(() => {
    if (!periodoInicio || execDisp.length === 0) return null;
    const inicio = new Date(periodoInicio + 'T00:00:00');
    let noPeriodo = 0;
    let retroativas = 0;
    let totalDefasagem = 0;
    for (const e of execDisp) {
      const de = new Date(e.data_execucao + 'T00:00:00');
      if (de >= inicio) {
        noPeriodo++;
      } else {
        retroativas++;
        totalDefasagem += Math.round((inicio.getTime() - de.getTime()) / 86400000);
      }
    }
    return {
      total: execDisp.length,
      noPeriodo,
      retroativas,
      defasagemMedia: retroativas > 0 ? Math.round(totalDefasagem / retroativas) : 0,
    };
  }, [execDisp, periodoInicio]);

  // A3+: Oldest available execution date for suggestion
  const oldestExecDate = execDisp.length > 0 ? execDisp[0].data_execucao : null;

  const handleSubmit = () => {
    if (!versaoVigente || !periodoInicio || !periodoFim) return;
    onSubmit({
      planilha_versao_id: versaoVigente.id,
      numero_boletim: nextNumber,
      periodo_inicio: periodoInicio,
      periodo_fim: periodoFim,
      observacoes: obs || undefined,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Novo Boletim de Medição #{nextNumber}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Início do Período</Label>
              <Input type="date" value={periodoInicio} onChange={e => setPeriodoInicio(e.target.value)} />
            </div>
            <div>
              <Label>Fim do Período</Label>
              <Input type="date" value={periodoFim} onChange={e => setPeriodoFim(e.target.value)} />
            </div>
          </div>

          {/* A3+: Period suggestion */}
          {oldestExecDate && !periodoInicio && (
            <div className="flex items-center gap-2 p-2.5 rounded-lg border border-primary/20 bg-primary/5 text-sm">
              <span className="text-primary">💡</span>
              <span className="text-muted-foreground">
                Execuções disponíveis desde <strong className="text-foreground">{format(new Date(oldestExecDate + 'T00:00:00'), 'dd/MM/yyyy', { locale: ptBR })}</strong>.
              </span>
              <Button variant="link" size="sm" className="ml-auto h-auto p-0 text-xs"
                onClick={() => setPeriodoInicio(oldestExecDate)}>
                Usar como início
              </Button>
            </div>
          )}

          {/* A3+: Eligibility summary */}
          {eligibility && eligibility.total > 0 && (
            <div className="p-3 rounded-lg border bg-muted/30 text-sm space-y-1">
              <p className="font-medium text-foreground flex items-center gap-1.5">
                ℹ️ Resumo de Elegibilidade
              </p>
              <p className="text-muted-foreground">
                <strong className="text-foreground">{eligibility.total}</strong> execução(ões) disponível(is) para medição
              </p>
              <p className="text-muted-foreground">
                <strong className="text-foreground">{eligibility.noPeriodo}</strong> no período selecionado
              </p>
              {eligibility.retroativas > 0 && (
                <p className="text-amber-600 dark:text-amber-400">
                  ⏪ <strong>{eligibility.retroativas}</strong> retroativa(s) — defasagem média: <strong>{eligibility.defasagemMedia} dias</strong>
                </p>
              )}
            </div>
          )}

          <div>
            <Label>Observações (opcional)</Label>
            <Textarea value={obs} onChange={e => setObs(e.target.value)} placeholder="Observações do boletim..." />
          </div>

          {/* A3+: Pre-confirmation message for retroactive */}
          {eligibility && eligibility.retroativas > 0 && (
            <div className="p-2.5 rounded-lg border border-amber-300/50 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-700/50 text-sm text-amber-700 dark:text-amber-300">
              ⚠️ Este boletim poderá incluir <strong>{eligibility.retroativas}</strong> execução(ões) retroativa(s) com defasagem média de <strong>{eligibility.defasagemMedia} dias</strong>.
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={handleSubmit} disabled={!periodoInicio || !periodoFim || isPending}>
            <Plus className="h-4 w-4 mr-1" /> Criar Boletim
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Legacy AddGlosaDialog removed — unified GlosaDialog is used instead

// --- Execuções Disponíveis Tab ---
function ExecucoesDisponiveisTab({ contratoId }: { contratoId: string }) {
  const { data: execucoes = [], isLoading } = useQuery({
    queryKey: ['available-executions', contratoId],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('emp_validacoes_execucao')
        .select('id, data_execucao, planilha_item_id, quantidade_executada, unidade, tipo, observacoes, localizacao_descricao, status, created_at, emp_planilha_itens!inner(codigo_item, descricao)')
        .eq('contrato_id', contratoId)
        .eq('status', 'disponivel_para_medicao')
        .is('boletim_id', null)
        .order('data_execucao', { ascending: false });
      if (error) throw error;
      return data as any[];
    },
    enabled: !!contratoId,
  });

  if (isLoading) return <p className="text-sm text-muted-foreground py-4">Carregando...</p>;

  if (execucoes.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <PackageCheck className="h-8 w-8 mx-auto mb-2 opacity-40" />
        <p className="text-sm font-medium">Nenhuma execução disponível para medição</p>
        <p className="text-xs mt-1">Execuções aparecem aqui após aprovação no Planejamento.</p>
      </div>
    );
  }

  const fmtDate = (d: string | null) => {
    if (!d) return '—';
    try { return format(new Date(d + (d.includes('T') ? '' : 'T00:00:00')), 'dd/MM/yyyy', { locale: ptBR }); }
    catch { return d; }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 p-3 rounded-lg border border-primary/20 bg-primary/5 text-sm">
        <PackageCheck className="h-4 w-4 text-primary flex-shrink-0" />
        <span className="text-muted-foreground">
          <strong className="text-foreground">{execucoes.length}</strong> execução(ões) aprovada(s) disponível(is) para medição neste contrato.
        </span>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Data Execução</TableHead>
            <TableHead>Item</TableHead>
            <TableHead>Tipo</TableHead>
            <TableHead className="text-right">Quantidade</TableHead>
            <TableHead>Unidade</TableHead>
            <TableHead>Local</TableHead>
            <TableHead>Observações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {execucoes.map(exec => (
            <TableRow key={exec.id}>
              <TableCell className="text-sm">{fmtDate(exec.data_execucao)}</TableCell>
              <TableCell className="text-xs">{(exec as any).emp_planilha_itens ? `${(exec as any).emp_planilha_itens.codigo_item} — ${(exec as any).emp_planilha_itens.descricao}` : '—'}</TableCell>
              <TableCell>
                <Badge variant={exec.tipo === 'retrabalho' ? 'destructive' : 'outline'} className="text-xs">
                  {exec.tipo === 'retrabalho' ? '↺ Retrabalho' : 'Execução'}
                </Badge>
              </TableCell>
              <TableCell className="text-right font-medium">{exec.quantidade_executada}</TableCell>
              <TableCell className="text-sm text-muted-foreground">{exec.unidade}</TableCell>
              <TableCell className="text-xs text-muted-foreground max-w-[150px] truncate">{exec.localizacao_descricao || '—'}</TableCell>
              <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">{exec.observacoes || '—'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
